"use client";
// Imports
import Link from "next/link";
import { useState, useRef, useCallback, useEffect } from "react";
import { formatShowLiveClockParts } from "@/lib/showDates";
import {
  setCurrentStepOnSlideChange,
  everySecondUpdateTheTime,
  getTodaysEvents,
  getToday,
} from "@/lib/helpers";
import {
  getLiveEvents,
  getFutureEvents,
  getUpcomingEvents,
  getEventTime,
  getEventTitle,
} from "@/lib/whats-on";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ComponentWrapper, Container, Icon, Img } from "@/components";
import { Button } from "@/components/ui/button";

// Whats On
export function WhatsOn({
  type,
  events,
  headline,
  showDates,
  testMode,
  testDate,
  mapIsActive = false,
}) {
  // Variables
  const [currentTime, setCurrentTime] = useState(null);
  const intervalRef = useRef(null);
  const [happeningNow, setHappeningNow] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [later, setLater] = useState(null);
  const [mobileTypeShowing, setMobileTypeShowing] = useState(1);

  // Today date
  const today = getToday(testMode, testDate);

  useEffect(() => {
    const todaysEvents = getTodaysEvents(events, showDates, today);

    let nowEvents = shuffleArray(getLiveEvents(todaysEvents, today));
    let upcomingEvents = shuffleArray(getUpcomingEvents(todaysEvents, today));
    let laterEvents = shuffleArray(getFutureEvents(todaysEvents, today));

    // Helper function to rotate an array by one position
    const rotateArray = (array) => {
      return array.length > 0 ? [...array.slice(1), array[0]] : array;
    };

    // Ensure the first event titles are different
    if (
      nowEvents.length > 0 &&
      upcomingEvents.length > 0 &&
      nowEvents[0].title === upcomingEvents[0].title
    ) {
      upcomingEvents = rotateArray(upcomingEvents);
    }

    if (
      nowEvents.length > 0 &&
      laterEvents.length > 0 &&
      nowEvents[0].title === laterEvents[0].title
    ) {
      laterEvents = rotateArray(laterEvents);
    }

    if (
      upcomingEvents.length > 0 &&
      laterEvents.length > 0 &&
      upcomingEvents[0].title === laterEvents[0].title
    ) {
      laterEvents = rotateArray(laterEvents);
    }

    setHappeningNow(nowEvents);
    setUpcoming(upcomingEvents);
    setLater(laterEvents);
  }, []);

  const updateTime = useCallback(() => {
    setCurrentTime(getToday(testMode, testDate));
  }, [testMode, testDate]);

  const shuffleArray = (array) => {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  everySecondUpdateTheTime(updateTime, currentTime, intervalRef);

  // Render
  return (
    <ComponentWrapper type={type}>
      <Container>
        <div className="relative p-6 opacity-100 lg:p-12 bg-background z-1 rounded-base">
          <div className="flex flex-col">
            <div className="flex flex-row justify-between lg:space-x-6 lg:items-center">
              <h2 className="level-2">{headline}</h2>
              <Link className="" href="/whats-on">
                <Button size="sm" variant="default">
                  See Full Schedule
                </Button>
              </Link>
            </div>
            {currentTime && <LiveDate currentTime={currentTime} />}
          </div>
          <div className="flex gap-2 py-4 justify-stretch lg:hidden">
            <button
              className={`font-bold whitespace-nowrap border-2 flex-1 rounded-lg px-4 py-2  ${
                mobileTypeShowing == 1
                  ? "bg-button text-button-foreground border-button"
                  : "bg-button-foreground border-button-foreground"
              }`}
              onClick={() => setMobileTypeShowing(1)}
            >
              On Now
            </button>
            <button
              className={`font-bold whitespace-nowrap border-2 flex-1 rounded-lg px-4 py-2  ${
                mobileTypeShowing == 2
                  ? "bg-button text-button-foreground border-button"
                  : "bg-button-foreground border-white"
              }`}
              onClick={() => setMobileTypeShowing(2)}
            >
              Upcoming
            </button>
            <button
              className={`font-bold whitespace-nowrap border-2 flex-1 rounded-lg px-4 py-2  ${
                mobileTypeShowing == 3
                  ? "bg-button text-button-foreground border-button"
                  : "bg-button-foreground border-white"
              }`}
              onClick={() => setMobileTypeShowing(3)}
            >
              Later
            </button>
          </div>
          <div className="relative flex items-center justify-center w-full h-[550px] lg:h-[750px] lg:py-10 grid-cols-12 overflow-hidden lg:grid lg:gap-6">
            <EventsCarousel
              type="now"
              label="Happening Now!"
              events={happeningNow}
              order={1}
              currentlyActiveIndex={mobileTypeShowing}
              today={today}
              mapIsActive={mapIsActive}
            />
            <EventsCarousel
              type="upcoming"
              label="Coming Up..."
              events={upcoming}
              order={2}
              currentlyActiveIndex={mobileTypeShowing}
              today={today}
              mapIsActive={mapIsActive}
            />
            <EventsCarousel
              type="later"
              label="Later On..."
              events={later}
              order={3}
              currentlyActiveIndex={mobileTypeShowing}
              today={today}
              mapIsActive={mapIsActive}
            />
          </div>
        </div>
      </Container>
    </ComponentWrapper>
  );
}

// Live Date
function LiveDate({ currentTime }) {
  if (!currentTime) return null;

  const clock = formatShowLiveClockParts(currentTime);

  return (
    <div className="pt-4">
      <p key="clock" className="large-p">
        <span className="font-bold level-2">
          {clock.hour}:{clock.minute}
          {clock.amPm}{" "}
        </span>
        <span>{clock.weekday}, </span>
        <span>
          {clock.day}
          {clock.ordinal}{" "}
        </span>
        <span>{clock.month}</span>
      </p>
    </div>
  );
}

// Events Carousel
function EventsCarousel({
  label,
  events,
  order,
  currentlyActiveIndex,
  today,
  type,
  mapIsActive = false,
}) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  setCurrentStepOnSlideChange(setCurrent, api);

  return (
    <div
      className={`absolute w-full h-full col-span-12 gap-12 lg:relative lg:col-span-4 ${
        currentlyActiveIndex == order && events?.length > 0
          ? "pointer-events-auto"
          : "pointer-events-none"
      } lg:pointer-events-auto`}
    >
      {events?.length > 0 && currentlyActiveIndex == order && (
        <div className="flex justify-center my-4 w-full lg:hidden lg:my-8">
          <button onClick={() => api?.scrollPrev()}>
            <Icon icon="up-arrow" />
          </button>
        </div>
      )}
      {events?.length > 0 && (
        <div className="hidden justify-center my-4 w-full lg:flex lg: lg:my-8">
          <button onClick={() => api?.scrollPrev()}>
            <Icon icon="up-arrow" />
          </button>
        </div>
      )}
      <Carousel
        setApi={setApi}
        opts={{
          align: "center",
          loop: true,
        }}
        loop={true}
        orientation="vertical"
        className={`overflow-hidden transition-all duration-300 ${
          currentlyActiveIndex == order
            ? "translate-x-0 opacity-100"
            : currentlyActiveIndex < order
            ? "translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100"
            : "-translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100"
        }`}
      >
        {events?.length > 0 && (
          <div className="flex">
            <p
              className={`hidden absolute top-6 left-6 z-20 px-4 py-1 font-bold rounded-full shadow-md backdrop-blur-md lg:flex text-card-foreground bg-brand-wool/50`}
            >
              {label}
            </p>
          </div>
        )}
        <CarouselContent
          className={`relative flex items-center ${
            events?.length > 0
              ? "h-[450px] lg:h-[500px]"
              : "h-[550px] lg:h-[670px]"
          }`}
        >
          {events?.length > 0 ? (
            events?.map((event, index) => (
              <Event
                key={index}
                event={event}
                current={current}
                index={index}
                label={label}
                today={today}
                type={type}
                mapIsActive={mapIsActive}
              />
            ))
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="max-w-[200px] text-center">
                No events at the moment, check back again later!
              </p>
            </div>
          )}
        </CarouselContent>
      </Carousel>
      {events?.length > 0 && currentlyActiveIndex == order && (
        <div className="flex justify-center my-4 w-full lg:hidden lg:my-8">
          <button onClick={() => api?.scrollNext()}>
            <Icon icon="down-arrow" />
          </button>
        </div>
      )}
      {events?.length > 0 && (
        <div className="hidden justify-center my-4 w-full lg:flex lg:my-8">
          <button onClick={() => api?.scrollNext()}>
            <Icon icon="down-arrow" />
          </button>
        </div>
      )}
    </div>
  );
}

function Event({ event, current, index, today, type, mapIsActive = false }) {
  const eventTime = getEventTime(event, today, type);
  const { title, description, image } = getEventTitle(event, today, type);
  const [showTooltip, setShowTooltip] = useState(false);

  // console.log("event", event);

  return (
    <CarouselItem
      key={index}
      className={`opacity-25 my-4 rounded-lg bg-card text-card-foreground h-full lg:max-w-none max-w-lg w-full transition-opacity duration-300 ${
        current === index + 1 ? "opacity-100" : "opacity-10"
      }`}
    >
      <div className="flex overflow-hidden relative flex-col justify-between mb-2 h-full rounded-lg">
        <div className="flex absolute top-2 left-2 z-20 gap-2 lg:flex-col lg:top-6 lg:right-6 lg:left-auto">
          {event?.tags?.length > 0 && (
            <div className="relative flex flex-wrap gap-2 max-w-[50%] lg:flex-col">
              {event.tags.length > 2
                ? null
                : event.tags.map((tag, tagIndex) => (
                    <p
                      key={tagIndex}
                      className="px-2 py-1 text-xs font-bold text-white rounded-full shadow-md backdrop-blur-md bg-brand-blue lg:px-4 lg:text-sm"
                    >
                      {tag?.title}
                    </p>
                  ))}
            </div>
          )}
        </div>
        <div className="relative h-[300px] lg:h-[400px]">
          <Img
            sizes="(max-width: 1024px) 100vw, 400px"
            className={`${event?.grid_image?.contain_image ? "p-4" : ""}`}
            data={image}
            fill={event?.grid_image?.contain_image ? false : true}
          />
          <div className="absolute top-2 right-2 px-2 py-1 border lg:hidden rounded-card-radius border-card bg-card-foreground">
            <p className="block text-card lg:hidden">{eventTime}</p>
          </div>
        </div>
        <div className="flex flex-col justify-between h-full min-h-[150px] lg:min-h-[250px] p-4 space-y-5 lg:p-6">
          {event?.show_as_page ? (
            <Link href={event?.permalink}>
              <h3 className="mb-2 level-3">{title}</h3>
              <p>
                {description?.length > 180
                  ? description.substring(0, 180) + "..."
                  : description}
              </p>
            </Link>
          ) : (
            <div className="text-left">
              <h3 className="mb-2 level-3">{title}</h3>
              <p>
                {description?.length > 180
                  ? description.substring(0, 180) + "..."
                  : description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 items-end lg:grid-cols-2">
            <p className="hidden lg:block">{eventTime}</p>
            {mapIsActive && event?.locations?.length > 0 && (
              <div className="flex justify-start items-center lg:justify-end">
                <Link
                  href={`/map?pin=${event?.locations[0]?.slug}`}
                  className="overflow-hidden px-4 text-center rounded-full border-2 transition-all duration-500 text-nowrap text-button lg:hover:bg-button lg:hover:text-button-foreground border-button"
                >
                  {event?.locations[0]?.title.length > 20
                    ? event?.locations[0]?.title.substring(0, 20) + "..."
                    : event?.locations[0]?.title}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </CarouselItem>
  );
}
